import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, Chip, Stack, CircularProgress, Alert,
  useMediaQuery, Collapse, IconButton, FormControlLabel, Checkbox,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import api from "../services/api";
import { todayISO } from "../utils/date";

/* =====================================================================
 * Employee loans.
 *
 * Staff borrow money and pay it back a bit each week. Those weekly
 * payments were already being typed into Daily Cash as Cash Received
 * against the employee's name, and advances as an Expense — so the money
 * was recorded all along, with nowhere to see what was left. Employee
 * accounts are deliberately kept out of the supplier/customer ledger, so
 * the arithmetic that would have answered it was hidden.
 *
 * This screen only reads that. Nobody has to change how they work: the
 * cashier carries on entering repayments in Daily Cash exactly as now.
 * ===================================================================== */

const money = (n) =>
  "$" + (Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EmployeeLoans() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [data, setData] = useState({ employees: [], totalOwed: 0, owing: 0 });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(null);      // name whose history is expanded
  const [history, setHistory] = useState({});  // name -> entries
  const [dialog, setDialog] = useState(null);  // { mode, name, ... }
  const [showHidden, setShowHidden] = useState(false);

  const flash = (severity, text) => { setMsg({ severity, text }); setTimeout(() => setMsg(null), 5000); };

  const load = useCallback(() => {
    setLoading(true);
    api.get("/employeeLoans")
      .then((r) => setData(r.data || { employees: [] }))
      .catch((e) => flash("error", e?.response?.data?.error || "Could not load."))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (name) => {
    if (open === name) { setOpen(null); return; }
    setOpen(name);
    if (history[name]) return;
    api.get(`/employeeLoans/${encodeURIComponent(name)}`)
      .then((r) => setHistory((h) => ({ ...h, [name]: r.data.entries || [] })))
      .catch(() => flash("error", "Could not load the history."));
  };

  /* Wages and management fees move through Daily Cash under the same name as a
   * loan would, so the incoming side reads as a repayment and the person shows
   * in credit. Saying so plainly beats inventing a loan to cancel it out. */
  const markNotALoan = async (name) => {
    const reason = window.prompt(
      `Take ${name} off the loan list?\n\n` +
      "Use this when their money through Daily Cash is wages or management " +
      "fees, not a loan. They are kept out of every total.\n\nWhy?",
      "Salary, not a loan");
    if (reason === null) return;
    try {
      const r = await api.post("/employeeLoans/notALoan", { name, reason });
      flash("success", r.data.message);
      load();
    } catch (e) { flash("error", e?.response?.data?.error || "Could not save."); }
  };

  const putBack = async (name) => {
    try {
      const r = await api.post("/employeeLoans/notALoan", { name, notALoan: false });
      flash("success", r.data.message);
      load();
    } catch (e) { flash("error", e?.response?.data?.error || "Could not save."); }
  };

  const submit = async () => {
    const d = dialog;
    try {
      if (d.mode === "setOwed") {
        const r = await api.post("/employeeLoans/setOwed", { name: d.name, owed: Number(d.amount) });
        flash("success", r.data.message);
      } else {
        const r = await api.post("/employeeLoans/entry", {
          name: d.name,
          amount: d.mode === "reduce" ? -Math.abs(Number(d.amount)) : Math.abs(Number(d.amount)),
          date: d.date, notes: d.notes, cash: d.mode === "advance" && d.cash,
        });
        flash("success", r.data.message);
      }
      setDialog(null);
      setHistory({});
      load();
    } catch (e) {
      flash("error", e?.response?.data?.error || "Could not save.");
    }
  };

  /* This list is employee advances and nothing else. Management accounts -
   * wages, fees, people who have left - never appear, not even under "Show
   * everyone", which means every EMPLOYEE rather than every account. They are
   * reachable only through the quiet link at the foot of the page, so a
   * mistake can still be undone without them being underfoot. */
  const rows = showAll
    ? data.employees
    : data.employees.filter((e) => e.owed > 0.005);
  const behind = data.employees.filter((e) => e.behind).length;
  /* Someone with repayments recorded but no loan amount set reads as a
   * negative - as though the company owed them. It almost always means an
   * older loan nobody has typed in yet, so say so rather than hiding it. */
  const unset = data.employees.filter((e) => e.owed < -0.005);

  const actions = (e) => (
    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
      <Button size="small" variant="outlined"
        onClick={() => setDialog({ mode: "setOwed", name: e.name, amount: String(e.owed) })}>
        Set amount
      </Button>
      <Button size="small" variant="outlined" color="warning"
        onClick={() => setDialog({ mode: "advance", name: e.name, amount: "", cash: true, date: todayISO() })}>
        Give advance
      </Button>
      <Button size="small" variant="outlined" color="success"
        onClick={() => setDialog({ mode: "reduce", name: e.name, amount: "", date: todayISO() })}>
        Forgive
      </Button>
      <Button size="small" color="inherit" onClick={() => markNotALoan(e.name)}>
        Not a loan
      </Button>
    </Stack>
  );

  return (
    <Box p={isMobile ? 1 : 3}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Employee Loans</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Weekly repayments are entered in <b>Daily Cash → Cash Received</b> under the
        employee's name, exactly as now. This screen adds them up.
      </Typography>

      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }}>{msg.text}</Alert>}

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap", gap: 1 }} alignItems="center">
        <Chip color="error" variant="outlined" label={`Owed to you: ${money(data.totalOwed)}`} />
        <Chip variant="outlined" label={`${data.owing} owing`} />
        {behind > 0 && <Chip color="warning" label={`${behind} behind`} />}
        {unset.length > 0 && (
          <Chip variant="outlined" color="info"
            onClick={() => setShowAll(true)}
            label={`${unset.length} paying with no loan set`} />
        )}
        <FormControlLabel
          control={<Checkbox checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
          label="Show everyone"
        />
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>

      {loading ? <CircularProgress size={24} /> : isMobile ? (
        <Stack spacing={1.5}>
          {rows.map((e) => (
            <Paper key={e.name} variant="outlined"
              sx={{ p: 1.5, borderColor: e.behind ? "warning.main" : undefined }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontWeight: 700 }}>{e.name}</Typography>
                <Typography sx={{ fontWeight: 700, color: e.owed > 0 ? "error.main" : "success.main" }}>
                  {money(e.owed)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Repaid {money(e.repaid)} over {e.repayments} payment{e.repayments === 1 ? "" : "s"}
                {" · "}last {e.lastRepaid || "never"}
                {e.behind && " · behind"}
              </Typography>
              <Box sx={{ mt: 1.5 }}>{actions(e)}</Box>
            </Paper>
          ))}
          {!rows.length && <Typography color="text.secondary">Nobody owes anything.</Typography>}
          {!showAll && unset.length > 0 && (
            <Alert severity="info">
              {unset.length} employee{unset.length === 1 ? " has" : "s have"} repayments recorded
              but no loan amount set — {unset.map((u) => u.name).join(", ")}. Tick
              <b> Show everyone</b> and use <b>Set amount</b> on each.
            </Alert>
          )}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 820 }}>
            <TableHead>
              <TableRow sx={{ background: "#eee" }}>
                <TableCell />
                <TableCell>Employee</TableCell>
                <TableCell align="right">Owes you</TableCell>
                <TableCell align="right">Repaid</TableCell>
                <TableCell align="right">Payments</TableCell>
                <TableCell>Last payment</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((e) => (
                <React.Fragment key={e.name}>
                  <TableRow hover sx={{ bgcolor: e.behind ? "warning.lighter" : undefined }}>
                    <TableCell padding="none">
                      <IconButton size="small" onClick={() => toggle(e.name)}>
                        {open === e.name ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {e.name}
                      {e.behind && <Chip size="small" color="warning" label="behind" sx={{ ml: 1 }} />}
                    </TableCell>
                    <TableCell align="right"
                      sx={{ fontWeight: 700, color: e.owed > 0 ? "error.main" : "success.main" }}>
                      {money(e.owed)}
                    </TableCell>
                    <TableCell align="right">{money(e.repaid)}</TableCell>
                    <TableCell align="right">{e.repayments}</TableCell>
                    <TableCell>
                      {e.lastRepaid || <span style={{ color: "#999" }}>never</span>}
                      {e.weeksQuiet != null && e.weeksQuiet > 1 && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {e.weeksQuiet} weeks ago
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{actions(e)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, border: 0 }}>
                      <Collapse in={open === e.name} unmountOnExit>
                        <Box sx={{ p: 2, bgcolor: "#fafafa" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>What</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell align="right">Owed after</TableCell>
                                <TableCell>Note</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {(history[e.name] || []).map((h, i) => (
                                <TableRow key={i}>
                                  <TableCell>{h.date}</TableCell>
                                  <TableCell>{h.kind}</TableCell>
                                  <TableCell align="right"
                                    sx={{ color: h.amount < 0 ? "success.main" : "error.main" }}>
                                    {money(h.amount)}
                                  </TableCell>
                                  <TableCell align="right">{money(h.balance)}</TableCell>
                                  <TableCell>{h.notes}</TableCell>
                                </TableRow>
                              ))}
                              {!(history[e.name] || []).length && (
                                <TableRow><TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">Nothing recorded.</Typography>
                                </TableCell></TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {!rows.length && (
                <TableRow><TableCell colSpan={7}>
                  <Typography color="text.secondary" sx={{ py: 2 }}>Nobody owes anything.</Typography>
                </TableCell></TableRow>
              )}
              {!showAll && unset.length > 0 && (
                <TableRow><TableCell colSpan={7} sx={{ border: 0 }}>
                  <Alert severity="info" sx={{ my: 1 }}>
                    {unset.length} employee{unset.length === 1 ? " has" : "s have"} repayments
                    recorded but no loan amount set — <b>{unset.map((u) => u.name).join(", ")}</b>.
                    Tick <b>Show everyone</b> above, then press <b>Set amount</b> on each.
                  </Alert>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {(data.excluded || []).length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Button size="small" color="inherit" sx={{ textTransform: "none", opacity: 0.6 }}
            onClick={() => setShowHidden(true)}>
            {data.excluded.length} account{data.excluded.length === 1 ? "" : "s"} hidden from this list
          </Button>
        </Box>
      )}

      <Dialog open={showHidden} onClose={() => setShowHidden(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Hidden from Employee Loans</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Management accounts, wages and fees. Their money moves through Daily Cash
            like a loan would, so they are kept out of this list and out of every total.
          </Typography>
          <Stack spacing={1}>
            {(data.excluded || []).map((e) => (
              <Stack key={e.name} direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{e.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{e.reason}</Typography>
                </Box>
                <Button size="small" onClick={() => { putBack(e.name); setShowHidden(false); }}>
                  Put back
                </Button>
              </Stack>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowHidden(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ---------- the one dialog, in three moods ---------- */}
      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        {dialog && (
          <>
            <DialogTitle>
              {dialog.mode === "setOwed" ? `What does ${dialog.name} owe?`
                : dialog.mode === "advance" ? `Give ${dialog.name} an advance`
                : `Reduce what ${dialog.name} owes`}
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {dialog.mode === "setOwed"
                  ? "Type what they owe today. The app works out the difference from what it has and records that, so nothing already entered is lost."
                  : dialog.mode === "advance"
                  ? "Money lent to them now. Leave the tick on and it also goes on today's cash sheet, so the register stays right."
                  : "Takes money off what they owe — a week written off, or a correction. No cash moves."}
              </Typography>
              <TextField
                autoFocus fullWidth type="number" label="Amount" sx={{ mb: 2 }}
                value={dialog.amount}
                onChange={(e) => setDialog({ ...dialog, amount: e.target.value })}
                inputProps={{ step: "0.01" }}
              />
              {dialog.mode !== "setOwed" && (
                <TextField
                  fullWidth type="date" label="Date" sx={{ mb: 2 }}
                  InputLabelProps={{ shrink: true }}
                  value={dialog.date}
                  onChange={(e) => setDialog({ ...dialog, date: e.target.value })}
                />
              )}
              {dialog.mode === "advance" && (
                <FormControlLabel
                  control={<Checkbox checked={!!dialog.cash}
                    onChange={(e) => setDialog({ ...dialog, cash: e.target.checked })} />}
                  label="Cash out of the drawer today"
                />
              )}
              {dialog.mode !== "advance" && (
                <TextField
                  fullWidth label="Note (optional)" multiline minRows={2}
                  value={dialog.notes || ""}
                  onChange={(e) => setDialog({ ...dialog, notes: e.target.value })}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)}>Cancel</Button>
              <Button variant="contained" onClick={submit}
                disabled={!dialog.amount || !Number.isFinite(Number(dialog.amount))}>
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
